<template>
  <div id="edit-order">
    <order-breadcrumbs
      :order="order"
      :meta="meta"
    />

    <validation-observer
      ref="editOrderValidation"
    >
      <form
        id="editOrderForm"
        @submit.prevent
      >
        <b-alert
          v-if="shouldShowCustomerOrderTypeAlert"
          show
          :variant="customerOrderTypeAlertVariant"
          class="alert-on-gray-bg mb-1 p-1"
          data-testid="customer-order-type-alert"
        >
          {{ customerOrderTypeAlertMessage }}
        </b-alert>

        <b-row class="justify-content-center">

          <b-col
            cols="12"
            :md="isMultiTransport ? 12 : 7"
          >

            <b-row class="justify-content-between h-100">
              <b-col cols="12">

                <b-card
                  :title="$t('order.label.base-order-info.title')"
                  class="card-justify"
                >
                  <b-card-text>
                    <rc-overlay :show="loading">
                      <b-row>
                        <b-col
                          v-if="isEventOrder"
                          cols="12"
                          class="pb-1"
                        >
                          <validation-provider
                            #default="{ errors }"
                            :name="$t('order.event-kind.title')"
                            :rules="isEventOrder ? 'required' : ''"
                          >
                            <label for="eventKind">
                              {{ $t('order.event-kind.title') }}
                            </label>
                            <v-select
                              id="eventKind"
                              v-model="order.eventKind"
                              :options="eventKindOptions"
                              :clearable="false"
                              :searchable="false"
                              :reduce="option => option.value"
                              :disabled="isDisabled(statusesWhenDispatcherCanEdit, ['ROLE_DISPATCHER'])"
                              @change="commitOrder()"
                            />
                            <small class="text-danger">{{ errors[0] }}</small>
                          </validation-provider>
                        </b-col>

                        <b-col
                          v-if="isMultiTransport"
                          cols="12"
                          class="pb-1"
                        >
                          <validation-provider
                            #default="{ errors }"
                            :name="$t('order.direction.label')"
                            :rules="isMultiTransport ? 'required' : ''"
                          >
                            <label for="multiTransportDirection">
                              {{ $t('order.direction.label') }}
                            </label>
                            <v-select
                              id="multiTransportDirection"
                              v-model="order.multiTransportDirection"
                              :options="directionOptions"
                              :clearable="false"
                              :searchable="false"
                              :reduce="option => option.value"
                              :disabled="isDisabled(statusesWhenDispatcherCanEdit, ['ROLE_DISPATCHER'])"
                              @input="onMultiTransportDirectionChange"
                            />
                            <small class="text-danger">{{ errors[0] }}</small>
                          </validation-provider>
                        </b-col>

                        <b-col
                          cols="12"
                          :md="isEventOrder ? 6 : 12"
                          class="pb-1"
                        >
                          <validation-provider
                            #default="{ errors }"
                            :name="$t('order.label.base-order-info.start-at')"
                            rules="required"
                          >
                            <label for="startAt">
                              {{ $t('order.label.base-order-info.start-at') }}
                            </label>
                            <flat-pickr
                              id="startAt"
                              v-model="order.startAt"
                              class="form-control"
                              :config="configDateTimeStartAtPicker"
                              :label="$t('order.label.base-order-info.start-at')"
                              :disabled="isDisabled(statusesWhenDispatcherCanEdit, ['ROLE_DISPATCHER'])"
                              @change="commitOrder()"
                            />
                            <small class="text-danger">{{ errors[0] }}</small>
                          </validation-provider>
                        </b-col>

                        <b-col
                          v-if="isEventOrder"
                          md="6"
                          class="pb-1"
                        >
                          <validation-provider
                            ref="eventEndValidation"
                            #default="{ errors }"
                            :name="$t('order.label.base-order-info.event-end-at')"
                            :rules="eventEndAtRules"
                          >
                            <label for="endAt">
                              {{ $t('order.label.base-order-info.event-end-at') }}
                            </label>
                            <flat-pickr
                              id="endAt"
                              v-model="order.endAt"
                              class="form-control"
                              :config="configDateTimeEndAtPicker"
                              :label="$t('order.label.base-order-info.event-end-at')"
                              :disabled="isDisabled(statusesWhenDispatcherCanEdit, ['ROLE_DISPATCHER'])"
                              @change="commitOrder()"
                            />
                            <small class="text-danger">{{ errors[0] }}</small>
                          </validation-provider>
                        </b-col>

                        <template v-if="showPlannerPrefillCard">
                          <b-col
                            cols="12"
                            :md="shouldShowPlanningDurationField ? 4 : 6"
                            class="pb-1"
                          >
                            <b-form-group
                              class="mb-0"
                              label-for="plannerAssignmentOffset"
                            >
                              <template #label>
                                <span class="d-inline-flex align-items-center">
                                  <span>{{ $t('order.planning.assignment-at') }}</span>
                                  <feather-icon
                                    :id="planningAssignmentHelpTooltipId"
                                    v-b-tooltip.hover.top="$t('order.planning.assignment-help')"
                                    icon="InfoIcon"
                                    size="14"
                                    class="text-muted ml-25 cursor-help"
                                  />
                                </span>
                              </template>
                              <v-select
                                id="plannerAssignmentOffset"
                                :value="planningAssignmentOffsetInput"
                                :options="planningAssignmentOffsetOptions"
                                :clearable="false"
                                :append-to-body="true"
                                :calculate-position="calculatePlanningDurationSelectPosition"
                                taggable
                                push-tags
                                :create-option="value => value"
                                :placeholder="$t('order.planning.duration-placeholder')"
                                @input="handlePlanningAssignmentOffsetInput"
                              />
                              <small
                                v-if="planningAssignmentOffsetValidationError"
                                class="text-danger d-block mt-50"
                              >
                                {{ planningAssignmentOffsetValidationError }}
                              </small>
                            </b-form-group>
                          </b-col>

                          <b-col
                            v-if="shouldShowPlanningDurationField"
                            cols="12"
                            md="4"
                            class="pb-1"
                          >
                            <b-form-group
                              class="mb-0"
                              label-for="plannerDurationMinutes"
                            >
                              <template #label>
                                <span class="d-inline-flex align-items-center">
                                  <span>{{ $t('order.planning.duration-minutes') }}</span>
                                  <feather-icon
                                    :id="planningDurationHelpTooltipId"
                                    v-b-tooltip.hover.top="$t('order.planning.duration-help')"
                                    icon="InfoIcon"
                                    size="14"
                                    class="text-muted ml-25 cursor-help"
                                  />
                                </span>
                              </template>
                              <v-select
                                id="plannerDurationMinutes"
                                :value="planningDurationInput"
                                :options="planningDurationOptions"
                                :clearable="false"
                                :append-to-body="true"
                                :calculate-position="calculatePlanningDurationSelectPosition"
                                taggable
                                push-tags
                                :create-option="value => value"
                                :placeholder="$t('order.planning.duration-placeholder')"
                                @input="handlePlanningDurationInput"
                              />
                              <small
                                v-if="planningDurationValidationError"
                                class="text-danger d-block mt-50"
                              >
                                {{ planningDurationValidationError }}
                              </small>
                            </b-form-group>
                          </b-col>

                          <b-col
                            cols="12"
                            :md="shouldShowPlanningDurationField ? 4 : 6"
                            class="pb-1"
                          >
                            <label for="plannerTeamDefinition">
                              {{ $t('order.planning.team-definition') }}
                            </label>
                            <v-select
                              id="plannerTeamDefinition"
                              :value="selectedPlanningTeamDefinitionId"
                              :options="planningTeamDefinitionOptions"
                              :clearable="false"
                              label="text"
                              :reduce="option => option.value"
                              :placeholder="$t('order.planning.select-team-definition')"
                              @input="handlePlanningTeamDefinitionChange"
                            />
                          </b-col>
                        </template>

                        <b-col
                          v-if="!order.private && !isEventOrder && !isMedicalCourier"
                          key="commissionedBy"
                          cols="6"
                          class="pb-1"
                        >
                          <validation-provider
                            #default="{ errors }"
                            :name="$t('order.label.base-order-info.commissioned-by')"
                            rules="max:50"
                          >
                            <label for="commissionedBy">
                              {{ $t('order.label.base-order-info.commissioned-by') }}
                            </label>
                            <b-form-input
                              id="commissionedBy"
                              v-model="order.commissionedBy"
                              type="text"
                              maxlength="50"
                              :placeholder="$t('order.label.base-order-info.commissioned-by')"
                              autocomplete="off"
                              :disabled="isDisabled(statusesWhenDispatcherCanEdit, ['ROLE_DISPATCHER'])"
                              @change="commitOrder()"
                            />
                            <small class="text-danger">{{ errors[0] }}</small>
                          </validation-provider>
                        </b-col>

                        <b-col
                          v-if="!order.private && !isEventOrder && !isMedicalCourier"
                          key="agreedWith"
                          cols="6"
                          class="pb-1"
                        >
                          <validation-provider
                            #default="{ errors }"
                            :name="$t('order.label.base-order-info.agreed-with')"
                            rules="max:50"
                          >
                            <label for="agreedWith">
                              {{ $t('order.label.base-order-info.agreed-with') }}
                            </label>
                            <b-form-input
                              id="agreedWith"
                              v-model="order.agreedWith"
                              type="text"
                              maxlength="50"
                              :placeholder="$t('order.label.base-order-info.agreed-with')"
                              autocomplete="off"
                              :disabled="isDisabled(statusesWhenDispatcherCanEdit, ['ROLE_DISPATCHER'])"
                              @change="commitOrder()"
                            />
                            <small class="text-danger">{{ errors[0] }}</small>
                          </validation-provider>
                        </b-col>

                        <b-col
                          v-if="!isEventOrder"
                          key="prioritySignals"
                          cols="12"
                          class="pb-50"
                        >
                          <b-form-checkbox
                            v-model="order.prioritySignals"
                            @change="commitOrder()"
                          >
                            {{ $t('order.label.base-order-info.priority-signals') }}
                          </b-form-checkbox>
                        </b-col>

                        <b-col
                          v-if="!isEventOrder"
                          key="absolutePunctuality"
                          cols="12"
                          class="pb-50"
                        >
                          <b-form-checkbox
                            v-model="order.absolutePunctuality"
                            @change="commitOrder()"
                          >
                            {{ $t('order.label.absolute-punctuality') }}
                          </b-form-checkbox>
                        </b-col>

                        <b-col
                          v-if="!isEventOrder && !isMedicalCourier && !isMultiTransport"
                          key="returnTransport"
                          cols="12"
                          class="pb-50"
                        >
                          <b-form-checkbox
                            v-model="order.returnTransport"
                            :disabled="isDisabled(statusesWhenDispatcherCanEdit, ['ROLE_DISPATCHER'])"
                            @change="commitOrder()"
                          >
                            {{ $t('order.label.base-order-info.return-transport') }}
                          </b-form-checkbox>
                        </b-col>

                        <b-col
                          v-if="!isEventOrder && !isMedicalCourier"
                          key="infectious"
                          cols="12"
                          class="pb-50"
                        >
                          <b-form-checkbox
                            v-model="order.infectious"
                            :disabled="isDisabled(statusesWhenDispatcherCanEdit, ['ROLE_DISPATCHER'])"
                            @change="commitOrder()"
                          >
                            {{ $t('order.label.base-order-info.infectious') }}
                          </b-form-checkbox>
                        </b-col>

                        <b-col
                          v-if="showCustomerAddressOptions && !isEventOrder"
                          key="differentClientInfo"
                          cols="12"
                          class="pb-50"
                        >
                          <b-form-checkbox
                            v-model="differentClientInfo"
                            :disabled="order.private || isDisabled(statusesWhenDispatcherCanEdit, ['ROLE_DISPATCHER'])"
                            @change="handleDifferentClientInfo"
                          >
                            {{ $t('order.label.transport-info.set-different-client-data') }}
                          </b-form-checkbox>
                        </b-col>

                        <b-col
                          v-if="showCustomerAddressOptions && !isEventOrder"
                          key="private"
                          cols="12"
                          class="pb-50"
                        >
                          <b-form-checkbox
                            v-model="order.private"
                            :disabled="isDisabled()"
                            @change="handleIsPrivate();"
                          >
                            {{ $t('order.label.base-order-info.private') }}
                          </b-form-checkbox>
                        </b-col>

                        <b-col
                          v-if="order.private || isEventOrder"
                          key="amountGross"
                          cols="6"
                          class="pb-1"
                        >
                          <validation-provider
                            #default="{ errors }"
                            :name="$t('order.label.base-order-info.amount-gross')"
                            :rules="amountGrossRules"
                          >
                            <label for="paymentAmountGross">
                              {{ $t('order.label.base-order-info.amount-gross') }}
                            </label>
                            <b-form-input
                              id="paymentAmountGross"
                              v-model="order.amountGross"
                              type="number"
                              maxlength="50"
                              :placeholder="$t('order.label.base-order-info.amount-gross')"
                              autocomplete="off"
                              :disabled="isDisabled(statusesWhenDispatcherCanEdit, ['ROLE_DISPATCHER'])"
                              @change="commitOrder"
                            />
                            <small class="text-danger">{{ errors[0] }}</small>
                          </validation-provider>
                        </b-col>

                        <b-col
                          v-if="order.private || isEventOrder"
                          key="paymentMethod"
                          cols="6"
                          class="pb-1"
                        >
                          <validation-provider
                            #default="{ errors }"
                            :name="$t('order.label.base-order-info.payment-method')"
                            :rules="fullValidation ? 'required' : ''"
                          >
                            <b-form-group>
                              <label for="paymentMethod">
                                {{ $t('order.label.base-order-info.payment-method') }}
                              </label>
                              <v-select
                                id="paymentMethod"
                                v-model="order.paymentMethod"
                                :placeholder="$t('order.label.base-order-info.payment-method')"
                                :options="paymentMethods"
                                :clearable="false"
                                :searchable="false"
                                :disabled="isDisabled(statusesWhenDispatcherCanEdit, ['ROLE_DISPATCHER'])"
                                :reduce="option => option.value"
                                @change="commitOrder"
                              />
                              <small class="text-danger">{{ errors[0] }}</small>
                            </b-form-group>

                          </validation-provider>

                        </b-col>

                      </b-row>
                    </rc-overlay>
                  </b-card-text>

                </b-card>
              </b-col>
              <b-col
                v-if="isMultiTransport && shouldShowCustomerAddressForm"
                cols="12"
              >
                <b-card
                  :title="$t('order.label.transport-info.client-data-title')"
                  class="card-justify"
                >
                  <b-card-text>
                    <rc-overlay :show="loading">
                      <address-form
                        v-model="order.customerAddress"
                        field="customerAddress"
                        :disabled="isDisabled()"
                        :address-details-value="typeof order.customerAddressDetails === 'number' ? order.customerAddressDetails : null"
                        @change="commitOrder()"
                        @address-details-selected="val => { order.customerAddressDetails = val; commitOrder() }"
                      />

                      <validation-provider
                        #default="{ errors }"
                        :name="$t('order.label.transport-info.client-data-title')"
                        :rules="fullValidation && !isEventOrder ? 'required' : ''"
                      >
                        <input
                          v-model="order.customerAddress.id"
                          type="hidden"
                        >
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>

                    </rc-overlay>
                  </b-card-text>
                </b-card>
              </b-col>

              <b-col
                v-if="isMultiTransport && normalizedMultiTransportDirection === 'outbound'"
                cols="12"
              >
                <b-card
                  :title="$t('order.label.transport-info.pickup-point-title')"
                  class="card-justify"
                >
                  <b-card-text>
                    <rc-overlay :show="loading">
                      <address-form
                        v-model="order.pickupAddress"
                        field="pickupAddress"
                        :disabled="isDisabled()"
                        :address-details-value="typeof order.pickupAddressDetails === 'number' ? order.pickupAddressDetails : null"
                        @change="commitOrder()"
                        @address-details-selected="val => { order.pickupAddressDetails = val; commitOrder() }"
                      />

                      <validation-provider
                        #default="{ errors }"
                        :name="$t('order.label.transport-info.pickup-point-title')"
                        :rules="fullValidation && normalizedMultiTransportDirection === 'outbound' ? 'required' : ''"
                      >
                        <input
                          v-model="order.pickupAddress.id"
                          type="hidden"
                        >
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>

                    </rc-overlay>
                  </b-card-text>
                </b-card>

              </b-col>

              <b-col
                v-if="!isEventOrder && !isMedicalCourier"
                cols="12"
              >
                <b-card
                  footer-tag="footer"
                  class="card-justify"
                >
                  <template #header>
                    <div class="d-flex flex-wrap justify-content-between align-items-center">
                      <span class="card-title mb-0">{{ $t('order.patient-info.title') }}</span>
                      <div
                        v-if="shouldShowTransportHistoryIndicator"
                        class="d-flex align-items-center"
                      >
                        <b-spinner
                          v-if="transportHistory.loading"
                          small
                          class="ml-50"
                        />
                        <b-button
                          v-if="transportHistory.initialized"
                          v-b-tooltip
                          size="sm"
                          :variant="transportHistoryBadgeVariant"
                          class="transport-history-badge ml-50"
                          pill
                          :title="transportHistoryTooltip"
                          @click="openTransportHistoryModal"
                        >
                          {{ transportHistory.displayCount }}
                        </b-button>
                      </div>
                    </div>
                    <small
                      v-if="transportHistory.error && transportHistoryRequestData"
                      class="text-danger d-block mt-50"
                    >
                      {{ $t('order.patient-info.transport-history.error') }}
                    </small>
                  </template>
                  <b-card-text>
                    <rc-overlay :show="loading">
                      <div v-if="activePatientEntries.length">
                        <section
                          v-for="(entry, patientIndex) in activePatientEntries"
                          :key="entry.patient.id || entry.index"
                          :class="hasMultipleActivePatients ? 'border rounded p-1 mb-1' : ''"
                        >
                          <div
                            v-if="hasMultipleActivePatients"
                            class="d-flex justify-content-between align-items-center mb-1"
                          >
                            <h5 class="mb-0">
                              {{ $t('order.patient-info.single-title', { index: patientIndex + 1 }) }}
                            </h5>
                            <b-button
                              v-if="!isDisabled(statusesWhenDispatcherCanEdit, ['ROLE_DISPATCHER'])"
                              size="sm"
                              variant="outline-danger"
                              @click="removePatient(entry.index)"
                            >
                              <feather-icon icon="Trash2Icon" />
                              {{ $t('order.patient-info.list.remove') }}
                            </b-button>
                          </div>
                          <b-row>
                            <b-col
                              cols="12"
                              :md="isMultiTransport ? 7 : 12"
                            >
                              <order-patient-form
                                :patient="entry.patient"
                                :patient-index="patientIndex"
                                :is-multi-transport="isMultiTransport"
                                :full-validation="fullValidation"
                                :pesel-reference-date="order.startAt"
                                :statuses-when-dispatcher-can-edit="statusesWhenDispatcherCanEdit"
                                :is-disabled-fn="(...args) => isDisabled(...args)"
                                :commit-handler="commitOrder"
                                :convert-from-annual="convertFromAnnual"
                              />
                            </b-col>
                            <b-col
                              v-if="isMultiTransport"
                              cols="12"
                              :md="isMultiTransport ? 5 : 12"
                            >
                              <address-form
                                v-model="entry.patient.address"
                                field="address"
                                :disabled="isDisabled()"
                                :address-details-value="typeof entry.patient.addressDetails === 'number' ? entry.patient.addressDetails : null"
                                :label="patientAddressLabel"
                                @change="commitOrder()"
                                @address-details-selected="val => { entry.patient.addressDetails = val; commitOrder() }"
                              />
                            </b-col>
                          </b-row>
                        </section>
                      </div>
                      <div
                        v-else
                        class="text-center py-2 text-muted"
                      >
                        {{ $t('order.patient-info.list.empty') }}
                      </div>
                      <div
                        v-if="removedPatientEntries.length"
                        class="mt-1"
                      >
                        <h6 class="mb-50 text-muted">
                          {{ $t('order.patient-info.list.removed-title') }}
                        </h6>
                        <div
                          v-for="entry in removedPatientEntries"
                          :key="`removed-${entry.patient.id || entry.index}`"
                          class="d-flex justify-content-between align-items-center border rounded p-50 mb-50"
                        >
                          <div>
                            {{ entry.patient.surname || '' }} {{ entry.patient.name || '' }}
                          </div>
                          <b-button
                            v-if="!isDisabled(statusesWhenDispatcherCanEdit, ['ROLE_DISPATCHER'])"
                            size="sm"
                            variant="outline-primary"
                            @click="restorePatient(entry.index)"
                          >
                            <feather-icon icon="RotateCcwIcon" />
                            {{ $t('order.patient-info.list.restore') }}
                          </b-button>
                        </div>
                      </div>
                    </rc-overlay>
                  </b-card-text>

                  <template
                    v-if="isMultiTransport"
                    #footer
                  >
                    <b-button
                      variant="flat-primary"
                      :disabled="loading || isDisabled(statusesWhenDispatcherCanEdit, ['ROLE_DISPATCHER'])"
                      @click="addPatient"
                    >
                      <feather-icon icon="PlusIcon" />
                      {{ $t('order.patient-info.list.add') }}
                    </b-button>
                  </template>
                </b-card>

                <b-modal
                  :visible="transportHistory.modalVisible"
                  :title="$t('order.patient-info.transport-history.modal-title')"
                  size="lg"
                  modal-class="transport-history-modal"
                  body-class="p-0"
                  ok-only
                  @hidden="closeTransportHistoryModal"
                >
                  <p class="text-muted p-1 pb-0 mb-0">
                    {{ transportHistoryRangeDescription }}
                  </p>

                  <p
                    v-if="transportHistory.truncated"
                    class="text-muted p-1 pb-0 mb-0"
                  >
                    {{ $t('order.patient-info.transport-history.truncated') }}
                  </p>

                  <div
                    v-if="transportHistory.items.length"
                    class="table-responsive"
                  >
                    <table class="table transport-history-table w-100 mb-0">
                      <thead>
                        <tr>
                          <th>{{ $t('order.patient-info.transport-history.columns.number') }}</th>
                          <th>{{ $t('order.patient-info.transport-history.columns.status') }}</th>
                          <th>{{ $t('order.patient-info.transport-history.columns.start-at') }}</th>
                          <th class="w-100">
                            {{ $t('order.patient-info.transport-history.columns.route') }}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="item in transportHistory.items"
                          :key="item.id"
                        >
                          <td class="text-nowrap">
                            <b-badge
                              variant="light-secondary"
                              class="font-medium-1"
                            >
                              {{ item.fullNumber || '-' }}
                            </b-badge>
                          </td>
                          <td class="text-nowrap">
                            <b-badge
                              variant="light-secondary"
                              class="font-medium-1"
                              :style="{ 'background-color': getTransportHistoryStatusColor(item.status) }"
                            >
                              {{ $t(`order.status.${item.status}`) }}
                            </b-badge>
                          </td>
                          <td>{{ formatTransportHistoryStartAt(item.startAt) }}</td>
                          <td class="text-wrap">
                            {{ item.route }}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <div
                    v-else
                    class="alert alert-info p-1 mb-0"
                  >
                    {{ $t('order.patient-info.transport-history.empty') }}
                  </div>
                </b-modal>
              </b-col>

              <b-col
                v-if="isMultiTransport && normalizedMultiTransportDirection === 'inbound'"
                cols="12"
              >
                <b-card
                  :title="deliveryCardTitle"
                  class="card-justify"
                >
                  <b-card-text>
                    <rc-overlay :show="loading">
                      <address-form
                        v-model="order.deliveryAddress"
                        field="deliveryAddress"
                        :disabled="isDisabled()"
                        :address-details-value="typeof order.deliveryAddressDetails === 'number' ? order.deliveryAddressDetails : null"
                        @change="commitOrder()"
                        @address-details-selected="val => { order.deliveryAddressDetails = val; commitOrder() }"
                      />

                      <validation-provider
                        #default="{ errors }"
                        :name="$t('order.label.transport-info.delivery-point-title')"
                        :rules="fullValidation && normalizedMultiTransportDirection === 'inbound' ? 'required' : ''"
                      >
                        <input
                          v-model="order.deliveryAddress.id"
                          type="hidden"
                        >
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>

                    </rc-overlay>
                  </b-card-text>
                </b-card>
              </b-col>

              <b-col cols="12">
                <order-custom-fields
                  :definitions="customFieldDefinitions"
                  :values="order.customFieldValues || {}"
                  :order-type="order.type"
                  :readonly="false"
                  :loading="loading"
                  @update:values="vals => { order.customFieldValues = vals; commitOrder() }"
                />
              </b-col>

              <b-col cols="12">
                <b-card
                  footer-tag="footer"
                  :title="$t('order.label.comment')"
                  class="card-justify"
                >
                  <b-card-text>
                    <rc-overlay :show="loading">
                      <b-row>
                        <b-col
                          cols="12"
                          class="pb-1"
                        >
                          <validation-provider
                            #default="{ errors }"
                            :name="$t('order.label.comment')"
                            :rules="'max:10000'"
                          >
                            <b-form-textarea
                              id="orderComment"
                              v-model="orderComment"
                              :placeholder="$t('order.label.comment')"
                              rows="3"
                              max-rows="20"
                              :disabled="!!order.fullNumber"
                              @change="commitOrder()"
                            />
                            <small class="text-danger">{{ errors[0] }}</small>
                          </validation-provider>
                        </b-col>
                      </b-row>
                    </rc-overlay>
                  </b-card-text>

                  <template #footer>
                    <div class="text-right">
                      <b-button
                        v-if="order.status === 'draft'"
                        class="mr-1"
                        variant="outline-danger"
                        :disabled="loading || lockSaveButton"
                        @click="confirmDelete"
                      >
                        <feather-icon icon="Trash2Icon" />
                        {{ $t('order.button.delete') }}
                      </b-button>

                      <b-button
                        variant="primary"
                        :disabled="loading || lockSaveButton || isCustomerOrderTypeBlockingSubmit"
                        @click="fullValidation = true; saveChanges(true)"
                      >
                        {{ $t('shared.button.submit') }}
                      </b-button>
                    </div>
                  </template>
                </b-card>
              </b-col>
            </b-row>
          </b-col>

          <b-col
            v-if="!isMultiTransport"
            cols="12"
            md="5"
          >

            <b-row class="justify-content-between h-100">
              <b-col
                v-if="!isEventOrder"
                cols="12"
              >
                <b-card class="card-justify">
                  <template #header>
                    <div class="d-flex align-items-center justify-content-between">
                      <span class="card-title mb-0">{{ $t('order.label.transport-info.pickup-point-title') }}</span>
                      <b-button
                        v-if="canSwapAddresses"
                        v-b-tooltip="$t('order.label.transport-info.swap-with-delivery-address')"
                        variant="flat-primary"
                        class="ml-50"
                        size="sm"
                        :aria-label="$t('order.label.transport-info.swap-with-delivery-address')"
                        data-testid="swap-order-addresses-pickup"
                        @click="handleSwapAddresses"
                      >
                        <feather-icon
                          icon="RefreshCwIcon"
                          size="16"
                        />
                      </b-button>
                    </div>
                  </template>
                  <b-card-text>
                    <rc-overlay :show="loading">
                      <address-form
                        v-model="order.pickupAddress"
                        field="pickupAddress"
                        :disabled="isDisabled()"
                        :address-details-value="typeof order.pickupAddressDetails === 'number' ? order.pickupAddressDetails : null"
                        @change="commitOrder()"
                        @address-details-selected="val => { order.pickupAddressDetails = val; commitOrder() }"
                      />

                      <validation-provider
                        #default="{ errors }"
                        :name="$t('order.label.transport-info.pickup-point-title')"
                        :rules="fullValidation ? 'required' : ''"
                      >
                        <input
                          v-model="order.pickupAddress.id"
                          type="hidden"
                        >
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>

                      <validation-provider
                        v-if="order.pickupAddress.id"
                        #default="{ errors }"
                        :name="$t('order.label.transport-info.pickup-details')"
                        rules="max:160"
                      >
                        <label
                          for="pickupDetails"
                          class="mt-1"
                        >
                          {{ $t('order.label.transport-info.pickup-details') }}
                        </label>
                        <b-form-input
                          id="pickupDetails"
                          v-model="order.pickupDetails"
                          type="text"
                          maxlength="160"
                          :placeholder="$t('order.label.transport-info.pickup-details')"
                          autocomplete="off"
                          :disabled="isDisabled(statusesWhenDispatcherCanEdit, ['ROLE_DISPATCHER'])"
                          @change="commitOrder()"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>

                    </rc-overlay>
                  </b-card-text>
                </b-card>
              </b-col>

              <b-col cols="12">
                <b-card
                  class="card-justify"
                >
                  <template #header>
                    <div class="d-flex align-items-center justify-content-between">
                      <span class="card-title mb-0">{{ deliveryCardTitle }}</span>
                      <b-button
                        v-if="canSwapAddresses"
                        v-b-tooltip="$t('order.label.transport-info.swap-with-pickup-address')"
                        variant="flat-primary"
                        size="sm"
                        class="ml-50"
                        :aria-label="$t('order.label.transport-info.swap-with-pickup-address')"
                        data-testid="swap-order-addresses-delivery"
                        @click="handleSwapAddresses"
                      >
                        <feather-icon
                          icon="RefreshCwIcon"
                          size="16"
                        />
                      </b-button>
                    </div>
                  </template>
                  <b-card-text>
                    <rc-overlay :show="loading">
                      <address-form
                        v-model="order.deliveryAddress"
                        field="deliveryAddress"
                        :disabled="isDisabled()"
                        :address-details-value="typeof order.deliveryAddressDetails === 'number' ? order.deliveryAddressDetails : null"
                        @change="commitOrder()"
                        @address-details-selected="val => { order.deliveryAddressDetails = val; commitOrder() }"
                      />

                      <validation-provider
                        #default="{ errors }"
                        :name="$t('order.label.transport-info.delivery-point-title')"
                        :rules="fullValidation ? 'required' : ''"
                      >
                        <input
                          v-model="order.deliveryAddress.id"
                          type="hidden"
                        >
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>

                      <validation-provider
                        v-if="order.deliveryAddress.id"
                        #default="{ errors }"
                        :name="$t('order.label.transport-info.delivery-details')"
                        rules="max:160"
                      >
                        <label
                          for="deliveryDetails"
                          class="mt-1"
                        >
                          {{ $t('order.label.transport-info.delivery-details') }}
                        </label>
                        <b-form-input
                          id="deliveryDetails"
                          v-model="order.deliveryDetails"
                          type="text"
                          maxlength="160"
                          :placeholder="$t('order.label.transport-info.delivery-details')"
                          autocomplete="off"
                          :disabled="isDisabled(statusesWhenDispatcherCanEdit, ['ROLE_DISPATCHER'])"
                          @change="commitOrder()"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>

                    </rc-overlay>
                  </b-card-text>
                </b-card>
              </b-col>

              <b-col
                v-if="shouldShowCustomerAddressForm"
                cols="12"
              >
                <b-card
                  :title="$t('order.label.transport-info.client-data-title')"
                  class="card-justify"
                >
                  <b-card-text>
                    <rc-overlay :show="loading">
                      <address-form
                        v-model="order.customerAddress"
                        field="customerAddress"
                        :disabled="isDisabled()"
                        :address-details-value="typeof order.customerAddressDetails === 'number' ? order.customerAddressDetails : null"
                        @change="commitOrder()"
                        @address-details-selected="val => { order.customerAddressDetails = val; commitOrder() }"
                      />

                      <validation-provider
                        #default="{ errors }"
                        :name="$t('order.label.transport-info.client-data-title')"
                        :rules="fullValidation && !isEventOrder ? 'required' : ''"
                      >
                        <input
                          v-model="order.customerAddress.id"
                          type="hidden"
                        >
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>

                    </rc-overlay>
                  </b-card-text>
                </b-card>
              </b-col>
            </b-row>

          </b-col>

        </b-row>
      </form>
    </validation-observer>
  </div>
</template>

<script>
import moment from 'moment'
import flatPickr from 'vue-flatpickr-component'
import 'flatpickr/dist/flatpickr.min.css'
import { Polish as PolishLocale } from 'flatpickr/dist/l10n/pl'
import { empty, cloneNested, parseRequestError } from '@/helpers/helpers'
import { VBTooltip } from 'bootstrap-vue'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import vSelect from 'vue-select'
import RcOverlay from '@/layouts/components/rc/RcOverlay.vue'
import OrderBreadcrumbs from '@/views/orders/partials/OrderBreadcrumbs.vue'
import OrderCustomFields from '@/views/orders/partials/OrderCustomFields.vue'
import guard from '@/guard'
import AddressForm from '@/views/orders/partials/AddressForm.vue'
import OrderPatientForm from '@/views/orders/partials/OrderPatientForm.vue'
import { createEmptyPatientInfo, patientTransportStatuses, resolvePatientTransportStatus } from '@/helpers/patient'
import { validatorPesel } from '@/@core-override/utils/validations/validators'
import orderTypes from '@/dictionaries/orderTypes'
import orderEventKinds from '@/dictionaries/orderEventKinds'
import multiTransportDirections from '@/dictionaries/multiTransportDirections'
import teamDefinitionRequests from '@/http/requests/settings/team-definition/team-definition'
import {
  formatPlanningMinutes,
  getPlanningAssignmentOffsetOptions,
  normalizePlanningTimeInput,
  PLANNING_TIME_SUGGESTIONS_MINUTES,
} from '@/helpers/orderPlanning'

export default {
  directives: {
    'b-tooltip': VBTooltip,
  },
  components: {
    AddressForm,
    OrderPatientForm,
    OrderBreadcrumbs,
    OrderCustomFields,

    RcOverlay,

    flatPickr,
    vSelect,

    ValidationProvider,
    ValidationObserver,
  },
  data() {
    return {
      loading: false,
      isSavingDraftOrder: false,
      lockSaveButton: false,
      fullValidation: false,
      differentClientInfo: false,
      saveChangesTimout: null,
      unsavedChanges: false,
      optimizingRoute: false,
      transportHistoryDebounceTimeout: null,
      transportHistoryRequestToken: 0,
      lastTransportHistoryRequestKey: null,
      plannerPrefillApplied: false,
      planningTeamDefinitions: [],
      planningAssignmentOffsetInput: null,
      planningAssignmentOffsetValidationError: '',
      planningDurationInput: null,
      planningDurationValidationError: '',
      customerOrderTypeAvailabilities: [],
      customerOrderTypeAvailabilityCustomerId: null,
      customerOrderTypeAvailabilityResolvedCustomerId: null,
      customerOrderTypeAvailabilityLoading: false,
      customerOrderTypeValidationInitialized: false,

      order: cloneNested(this.$store.getters['order/getOrderData']),

      meta: {
        breadcrumb: [
          { text: 'order.orders_title', to: '/order/list' },
          { text: 'order.edit_order', active: true },
        ],
        pageTitle: 'order.edit_order',
      },
      paymentMethods: [
        {
          value: null,
          label: this.$t('shared.select'),
        },
        {
          value: 'bank-transfer',
          label: this.$t('order.label.base-order-info.payment-method-bank-transfer'),
        },
        {
          value: 'card',
          label: this.$t('order.label.base-order-info.payment-method-card'),
        },
        {
          value: 'cash',
          label: this.$t('order.label.base-order-info.payment-method-cash'),
        },
      ],
      statusesWhenDispatcherCanEdit: [
        'received',
        'confirmed',
        'shipment_received',
        'shipment_delivered',
        'on_way_to_pickup_point',
        'at_pickup_point',
        'patient_pick_up',
        'on_way_to_delivery_point',
        'at_delivery_point',
        'patient_delivery',
        'patient_not_taken',
        'patient_left_at_destination',
        'completed',
      ],
    }
  },
  computed: {
    effectiveCustomerOrderTypeValidationAddress() {
      if (!this.differentClientInfo && this.showCustomerAddressOptions && !this.isEventOrder) {
        return this.order?.pickupAddress || this.order?.customerAddress || null
      }

      return this.order?.customerAddress || this.order?.pickupAddress || null
    },
    orderEdited() {
      return this.$store.getters['order/getOrderData']
    },
    transportHistory() {
      return this.$store.getters['order/getTransportHistory']
    },
    orderTypeValidationCustomer() {
      const customerAddressCustomer = this.effectiveCustomerOrderTypeValidationAddress?.customer
      if (customerAddressCustomer && typeof customerAddressCustomer === 'object') {
        return customerAddressCustomer
      }

      return null
    },
    customerOrderTypeValidationCustomerId() {
      return this.orderTypeValidationCustomer?.id || null
    },
    customerOrderTypeValidationMode() {
      return this.organizationConfig?.order?.customerOrderTypeValidation?.mode || 'off'
    },
    isCustomerOrderTypeValidationEnabled() {
      return this.customerOrderTypeValidationMode !== 'off'
    },
    isCustomerOrderTypeValidationReady() {
      if (!this.isCustomerOrderTypeValidationEnabled) {
        return false
      }

      if (!this.customerOrderTypeValidationInitialized) {
        return false
      }

      const customerId = this.customerOrderTypeValidationCustomerId
      if (!customerId) {
        return true
      }

      return !this.customerOrderTypeAvailabilityLoading
        && this.customerOrderTypeAvailabilityResolvedCustomerId === customerId
    },
    hasCustomerOrderTypeValidationAddressSelected() {
      return [this.effectiveCustomerOrderTypeValidationAddress].some(address => {
        if (!address || typeof address !== 'object') {
          return false
        }

        if (!empty(address.id) || !empty(address.address) || !empty(address.city) || !empty(address.zip)) {
          return true
        }

        const { customer } = address
        if (!customer) {
          return false
        }

        if (typeof customer === 'object') {
          return !empty(customer.id)
        }

        return !empty(customer)
      })
    },
    customerOrderTypeAvailabilityValidationResult() {
      if (!this.isCustomerOrderTypeValidationEnabled) {
        return null
      }

      if (!this.order?.type || !this.order?.startAt) {
        return null
      }

      if (!this.hasCustomerOrderTypeValidationAddressSelected) {
        return null
      }

      if (!this.orderTypeValidationCustomer?.id) {
        return {
          allowed: false,
          reason: 'customer_unknown',
        }
      }

      const normalizedStartAt = moment(this.order.startAt)
      if (!normalizedStartAt.isValid()) {
        return null
      }

      if (!Array.isArray(this.customerOrderTypeAvailabilities)) {
        return null
      }

      const orderDate = normalizedStartAt.format('YYYY-MM-DD')
      const matchingAvailability = this.customerOrderTypeAvailabilities.find(
        availability => availability.orderType === this.order.type,
      )

      if (!matchingAvailability) {
        return {
          allowed: false,
          reason: 'missing_configuration',
        }
      }

      if (orderDate < matchingAvailability.activeFrom || orderDate > matchingAvailability.activeTo) {
        return {
          allowed: false,
          reason: 'date_out_of_range',
        }
      }

      return {
        allowed: true,
        reason: 'allowed',
      }
    },
    shouldShowCustomerOrderTypeAlert() {
      return this.isCustomerOrderTypeValidationReady
        && this.customerOrderTypeAvailabilityValidationResult?.allowed === false
    },
    customerOrderTypeAlertVariant() {
      if (this.customerOrderTypeAvailabilityValidationResult?.reason === 'missing_configuration') {
        return this.customerOrderTypeValidationMode === 'blocking' ? 'danger' : 'warning'
      }

      if (
        this.customerOrderTypeAvailabilityValidationResult?.reason === 'customer_unknown'
        && this.customerOrderTypeValidationMode !== 'blocking'
      ) {
        return 'info'
      }

      return 'danger'
    },
    customerOrderTypeAlertMessage() {
      if (this.customerOrderTypeAvailabilityValidationResult?.reason === 'customer_unknown') {
        return this.$t(
          this.customerOrderTypeValidationMode === 'blocking'
            ? 'order.customer-order-type-validation.customer-not-found-blocking'
            : 'order.customer-order-type-validation.customer-not-found-warning',
          {
            address: this.customerOrderTypeValidationAddressLabel,
          },
        )
      }

      if (this.customerOrderTypeAvailabilityValidationResult?.reason === 'date_out_of_range') {
        return this.$t(
          this.customerOrderTypeValidationMode === 'blocking'
            ? 'order.customer-order-type-validation.date-out-of-range-blocking'
            : 'order.customer-order-type-validation.date-out-of-range-warning',
        )
      }

      return this.$t(
        this.customerOrderTypeValidationMode === 'blocking'
          ? 'order.customer-order-type-validation.missing-configuration-blocking'
          : 'order.customer-order-type-validation.missing-configuration-warning',
      )
    },
    isCustomerOrderTypeBlockingSubmit() {
      return this.order?.status === 'draft'
        && this.customerOrderTypeValidationMode === 'blocking'
        && this.customerOrderTypeAvailabilityValidationResult?.allowed === false
    },
    orderTypes() {
      return orderTypes
    },
    eventKinds() {
      return orderEventKinds
    },
    eventKindOptions() {
      return Object.values(this.eventKinds).map(kind => ({
        value: kind,
        label: this.$t(`order.event-kind.${kind}`),
      }))
    },
    directionOptions() {
      return Object.values(multiTransportDirections).map(direction => ({
        value: direction,
        label: this.$t(`order.direction.${direction}`),
      }))
    },
    isEventOrder() {
      return this.order && this.order.type === orderTypes.event
    },
    isMedicalCourier() {
      return this.order && this.order.type === orderTypes.medical_courier
    },
    isMultiTransport() {
      return this.order && this.order.type === orderTypes.multi_transport
    },
    canSwapAddresses() {
      if (!this.order || this.order.status !== 'draft' || this.hasMultipleActivePatients) {
        return false
      }

      if (!this.resolveAddressId(this.order.pickupAddress) || !this.resolveAddressId(this.order.deliveryAddress)) {
        return false
      }

      return [
        orderTypes.specialized_transport,
        orderTypes.basic_transport,
        orderTypes.sanitary_transport,
        orderTypes.medical_courier,
      ].includes(this.order.type)
    },
    activePatientEntries() {
      if (!Array.isArray(this.order?.patientInfo)) {
        return []
      }
      return this.order.patientInfo
        .map((patient, index) => ({ patient, index }))
        .filter(entry => resolvePatientTransportStatus(entry.patient) !== patientTransportStatuses.removedAfterConfirmation)
    },
    removedPatientEntries() {
      if (!Array.isArray(this.order?.patientInfo)) {
        return []
      }
      return this.order.patientInfo
        .map((patient, index) => ({ patient, index }))
        .filter(entry => resolvePatientTransportStatus(entry.patient) === patientTransportStatuses.removedAfterConfirmation)
    },
    hasMultipleActivePatients() {
      return this.activePatientEntries.length > 1
    },
    primaryPatientEntry() {
      return this.activePatientEntries.length ? this.activePatientEntries[0] : null
    },
    transportHistoryCustomer() {
      const customerAddressCustomer = this.order?.customerAddress?.customer
      if (customerAddressCustomer && typeof customerAddressCustomer === 'object') {
        return customerAddressCustomer
      }

      const pickupAddressCustomer = this.order?.pickupAddress?.customer
      if (pickupAddressCustomer && typeof pickupAddressCustomer === 'object') {
        return pickupAddressCustomer
      }

      return null
    },
    transportHistoryRequestData() {
      if (!this.order) {
        return null
      }

      if (![
        orderTypes.specialized_transport,
        orderTypes.basic_transport,
        orderTypes.sanitary_transport,
      ].includes(this.order.type)) {
        return null
      }

      if (!this.transportHistoryCustomer || !this.transportHistoryCustomer.isPoz) {
        return null
      }

      const customerId = this.transportHistoryCustomer.id || null
      const orderId = Number.isInteger(this.order.id) && this.order.id > 0 ? this.order.id : null
      const pickupAddressId = this.resolveAddressId(this.order.pickupAddress)
      const deliveryAddressId = this.resolveAddressId(this.order.deliveryAddress)
      const primaryPatient = this.primaryPatientEntry?.patient || null
      const pesel = primaryPatient?.patientData?.noPesel
        ? null
        : (primaryPatient?.pesel || null)

      if (!customerId || !pickupAddressId || !deliveryAddressId || !pesel || !validatorPesel(pesel)) {
        return null
      }

      return {
        orderId,
        customerId,
        pesel,
        pickupAddressId,
        deliveryAddressId,
      }
    },
    shouldShowTransportHistoryIndicator() {
      return [
        orderTypes.specialized_transport,
        orderTypes.basic_transport,
        orderTypes.sanitary_transport,
      ].includes(this.order?.type)
        && !!this.transportHistoryCustomer?.isPoz
        && (!!this.transportHistoryRequestData || this.transportHistory.loading || this.transportHistory.initialized)
    },
    transportHistoryBadgeVariant() {
      return this.transportHistory.count > 0 ? 'warning' : 'success'
    },
    transportHistoryLookbackMonths() {
      return this.normalizePozTransportHistoryLookbackMonths(
        this.organizationConfig?.order?.pozTransportHistoryLookbackMonths,
      )
    },
    transportHistoryTooltip() {
      return this.$t('order.patient-info.transport-history.tooltip', {
        months: this.transportHistoryLookbackMonths,
      })
    },
    transportHistoryRangeDescription() {
      return this.$t('order.patient-info.transport-history.range-description', {
        months: this.transportHistoryLookbackMonths,
      })
    },
    customerOrderTypeValidationAddressLabel() {
      return this.formatOrderAddress(this.effectiveCustomerOrderTypeValidationAddress)
        || this.$t('order.customer-order-type-validation.unknown-address')
    },
    normalizedMultiTransportDirection() {
      return this.resolveMultiTransportDirection(this.order?.multiTransportDirection)
    },
    patientAddressLabel() {
      return this.normalizedMultiTransportDirection === 'outbound'
        ? 'Miejsce docelowe pacjenta'
        : 'Miejsce odbioru pacjenta'
    },
    me() {
      return this.$store.getters['auth/getUser']
    },
    organizationConfig() {
      return this.$store.getters['appConfig/config'] || { canApplyCustomerAddressToOrderManually: false }
    },
    customFieldDefinitions() {
      return this.organizationConfig?.order?.customFields || []
    },
    showCustomerAddressOptions() {
      return this.organizationConfig.canApplyCustomerAddressToOrderManually
    },
    shouldShowCustomerAddressForm() {
      if (this.isEventOrder) {
        return true
      }
      return this.showCustomerAddressOptions && this.differentClientInfo
    },
    deliveryCardTitle() {
      return this.isEventOrder
        ? this.$t('order.label.transport-info.event-location-title')
        : this.$t('order.label.transport-info.delivery-point-title')
    },
    amountGrossRules() {
      if (!this.fullValidation) {
        return 'max:7'
      }

      return 'required|max:7'
    },
    eventEndAtRules() {
      if (!this.isEventOrder) {
        return ''
      }
      return {
        required: true,
        event_end_after: { target: this.order.startAt },
      }
    },
    orderComment: {
      get() {
        if (!this.order || !Array.isArray(this.order.comments) || !this.order.comments.length) {
          return ''
        }
        const [firstComment] = this.order.comments
        return (firstComment && firstComment.comment) || ''
      },
      set(value) {
        if (!this.order) {
          return
        }
        if (!Array.isArray(this.order.comments)) {
          this.$set(this.order, 'comments', [])
        }
        if (!this.order.comments.length) {
          this.order.comments.push({ comment: '' })
        }
        if (!this.order.comments[0]) {
          this.$set(this.order.comments, 0, { comment: '' })
        }
        this.$set(this.order.comments[0], 'comment', value)
      },
    },
    configDateTimeStartAtPicker() {
      return {
        locale: PolishLocale,
        enableTime: true,
        dateFormat: 'Y-m-d H:i',
        disableMobile: true,
        maxDate: this.order.endAt ? this.order.endAt : null,
      }
    },
    configDateTimeEndAtPicker() {
      return {
        locale: PolishLocale,
        enableTime: true,
        dateFormat: 'Y-m-d H:i',
        disableMobile: true,
        minDate: this.order.startAt,
      }
    },
    plannerPrefill() {
      const {
        plannerStartAt,
        plannerTeamDefinitionId,
      } = this.$route.query

      if (!plannerStartAt) {
        return null
      }

      const parsedStartAt = moment.parseZone(plannerStartAt)
      if (!parsedStartAt.isValid()) {
        return null
      }

      return {
        plannedTeamDefinition: plannerTeamDefinitionId
          ? {
            id: Number(plannerTeamDefinitionId),
            code: '',
          }
          : null,
        plannedAssignmentOffsetMinutes: Number(this.$route.query.plannerAssignmentOffsetMinutes || 0),
        plannedDurationMinutes: Number(this.$route.query.plannerDurationMinutes || 60),
        startAt: parsedStartAt.format('YYYY-MM-DD HH:mm'),
      }
    },
    selectedPlanningTeamDefinitionId() {
      return this.order?.plannedTeamDefinition?.id || this.plannerPrefill?.plannedTeamDefinition?.id || null
    },
    planningTeamDefinitionOptions() {
      return this.planningTeamDefinitions.map(teamDefinition => ({
        value: teamDefinition.id,
        text: teamDefinition.code,
      }))
    },
    planningDurationOptions() {
      const options = PLANNING_TIME_SUGGESTIONS_MINUTES
        .map(this.formatPlanningDurationMinutes)
        .filter(Boolean)

      if (this.planningDurationInput && !options.includes(this.planningDurationInput)) {
        return options.concat(this.planningDurationInput)
      }

      return options
    },
    planningAssignmentOffsetOptions() {
      const options = getPlanningAssignmentOffsetOptions(this.formatPlanningDurationMinutes)

      if (this.planningAssignmentOffsetInput && !options.includes(this.planningAssignmentOffsetInput)) {
        return options.concat(this.planningAssignmentOffsetInput)
      }

      return options
    },
    autoCalculatedPlanningDurationMinutes() {
      if (!this.order?.startAt || !this.order?.endAt) {
        return null
      }

      const startAt = moment(this.order.startAt)
      const endAt = moment(this.order.endAt)

      if (!startAt.isValid() || !endAt.isValid() || !endAt.isAfter(startAt)) {
        return null
      }

      return endAt.diff(startAt, 'minutes') + 30
    },
    hasAutoCalculatedPlanningDuration() {
      return this.autoCalculatedPlanningDurationMinutes !== null
    },
    shouldShowPlanningDurationField() {
      return !this.isEventOrder && !this.hasAutoCalculatedPlanningDuration
    },
    hasExistingPlanning() {
      return Boolean(
        this.order?.plannedTeamDefinition?.id
        || this.order?.plannedAssignmentOffsetMinutes !== null
        || this.order?.plannedDurationMinutes !== null,
      )
    },
    showPlannerPrefillCard() {
      return Boolean(this.plannerPrefill || this.hasExistingPlanning)
    },
    planningAssignmentHelpTooltipId() {
      return 'order-edit-planning-assignment-help'
    },
    planningDurationHelpTooltipId() {
      return 'order-edit-planning-duration-help'
    },
  },
  watch: {
    orderEdited: {
      deep: true,
      handler(newOrder) {
        if (this.shouldPreserveLocalDraftOrder(newOrder)) {
          this.applyOrderSaveGeneratedValues(newOrder)
        } else {
          this.order = cloneNested(newOrder)
        }
        this.normalizePatientAddressKeys()
        this.applyEventDefaults()
        this.enforceMultiTransportRestrictions()
        this.syncPlanningInputsFromOrder()
        this.applyPlannerPrefill()
        this.ensurePlanningTeamDefinitionsLoaded()
        this.scheduleTransportHistoryCheck()
        if (this.customerOrderTypeValidationInitialized) {
          this.syncCustomerOrderTypeAvailabilities()
        }
      },
    },
    'order.startAt': function orderStartAtWatcher() {
      this.syncAutoCalculatedPlanningDuration()

      if (this.isEventOrder && this.$refs.eventEndValidation) {
        this.$nextTick(() => {
          this.$refs.eventEndValidation.validate()
        })
      }
    },
    'order.endAt': function orderEndAtWatcher() {
      this.syncAutoCalculatedPlanningDuration()

      if (this.isEventOrder && this.$refs.eventEndValidation) {
        this.$nextTick(() => {
          this.$refs.eventEndValidation.validate()
        })
      }
    },
    customerOrderTypeValidationCustomerId() {
      if (this.customerOrderTypeValidationInitialized) {
        this.syncCustomerOrderTypeAvailabilities()
      }
    },
  },
  beforeMount() {
    this.initOrderData()
  },
  beforeDestroy() {
    clearTimeout(this.transportHistoryDebounceTimeout)
    this.$store.commit('order/CLEAR_ORDER_EDITED')
  },
  methods: {
    shouldPreserveLocalDraftOrder(newOrder) {
      if (!this.isSavingDraftOrder) {
        return false
      }

      const currentOrderId = this.order?.id
      const incomingOrderId = newOrder?.id

      if (empty(currentOrderId) || empty(incomingOrderId)) {
        return false
      }

      return String(currentOrderId) === String(incomingOrderId)
    },
    resolveAddressId(address) {
      if (empty(address)) {
        return null
      }
      if (typeof address === 'object') {
        return address.id || null
      }
      return address
    },
    resolveMultiTransportDirection(direction) {
      if (!direction) {
        return null
      }

      if (typeof direction === 'string') {
        const normalized = direction.toLowerCase()
        return normalized === 'inbound' || normalized === 'outbound' ? normalized : null
      }

      if (typeof direction === 'object') {
        const possible = [
          direction.value,
          direction.key,
          direction.code,
          direction.name,
          direction.direction,
          direction.id,
        ]
        for (let index = 0; index < possible.length; index += 1) {
          const resolved = this.resolveMultiTransportDirection(possible[index])
          if (resolved) {
            return resolved
          }
        }
      }

      return null
    },
    hasPatientAddress(patient) {
      if (!patient) {
        return false
      }
      return !!patient.address
    },
    normalizePatientAddressKeys() {
      if (!Array.isArray(this.order?.patientInfo)) {
        return
      }

      const normalizedDirection = this.resolveMultiTransportDirection(this.order.multiTransportDirection)

      this.order.patientInfo.forEach(patient => {
        if (!patient) {
          return
        }

        if (!Object.prototype.hasOwnProperty.call(patient, 'address')) {
          this.$set(patient, 'address', null)
        }

        if (!Object.prototype.hasOwnProperty.call(patient, 'addressDetails')) {
          this.$set(patient, 'addressDetails', null)
        }
      })

      if (!this.isMultiTransport) {
        return
      }

      const hasPatientAddress = this.order.patientInfo.some(patient => this.hasPatientAddress(patient))

      if (normalizedDirection === 'inbound' || normalizedDirection === 'outbound') {
        this.$set(this.order, 'multiTransportDirection', normalizedDirection)
        return
      }

      if (hasPatientAddress) {
        this.$set(this.order, 'multiTransportDirection', 'inbound')
      }
    },
    applyEventDefaults() {
      if (!this.isEventOrder) {
        return
      }

      if (!this.order.eventKind) {
        this.$set(this.order, 'eventKind', 'medical_coverage')
      }

      this.syncAutoCalculatedPlanningDuration()
    },
    enforceMultiTransportRestrictions() {
      if (!this.isMultiTransport) {
        return
      }

      if (this.order.returnTransport !== false) {
        this.$set(this.order, 'returnTransport', false)
      }
    },
    swapOrderAddresses(shouldCommit = true) {
      const cloneOrderField = value => (value === undefined ? undefined : cloneNested(value))
      const pickupAddress = cloneOrderField(this.order.pickupAddress)
      const deliveryAddress = cloneOrderField(this.order.deliveryAddress)
      const pickupAddressDetails = cloneOrderField(this.order.pickupAddressDetails)
      const deliveryAddressDetails = cloneOrderField(this.order.deliveryAddressDetails)
      const pickupDetails = cloneOrderField(this.order.pickupDetails)
      const deliveryDetails = cloneOrderField(this.order.deliveryDetails)

      this.$set(this.order, 'pickupAddress', deliveryAddress)
      this.$set(this.order, 'deliveryAddress', pickupAddress)
      this.$set(this.order, 'pickupAddressDetails', deliveryAddressDetails)
      this.$set(this.order, 'deliveryAddressDetails', pickupAddressDetails)
      this.$set(this.order, 'pickupDetails', deliveryDetails)
      this.$set(this.order, 'deliveryDetails', pickupDetails)

      if (shouldCommit) {
        this.commitOrder()
      }
    },
    handleSwapAddresses() {
      if (!this.canSwapAddresses) {
        return
      }

      this.swapOrderAddresses()
    },
    onMultiTransportDirectionChange(direction) {
      if (this.isMultiTransport) {
        if (direction) {
          this.$set(this.order, 'multiTransportDirection', direction)
        }

        this.swapOrderAddresses(false)

        if (Array.isArray(this.order.patientInfo) && this.order.patientInfo.length > 1) {
          const reversedPatients = cloneNested(this.order.patientInfo)
            .reverse()
            .map((patient, index) => ({
              ...patient,
              position: index + 1,
            }))

          this.$set(this.order, 'patientInfo', reversedPatients)
        }
      }

      this.commitOrder()
    },
    initOrderData() {
      this.loading = true
      this.$store.dispatch('order/fetchOrderById', this.$route.params.id)
        .then(res => {
          this.loading = false

          if (this.order.status !== 'draft' && !guard.hasAtLeastOneRole(['ROLE_ADVANCED_DISPATCHER'])) {
            this.redirectToView()
          }

          this.order.pickupAddressDetails = res.data.pickupAddressDetails ? res.data.pickupAddressDetails.id : null
          this.order.deliveryAddressDetails = res.data.deliveryAddressDetails ? res.data.deliveryAddressDetails.id : null
          this.order.customerAddressDetails = res.data.customerAddressDetails ? res.data.customerAddressDetails.id : null

          const customerAddressId = this.resolveAddressId(res.data.customerAddress)
          const pickupAddressId = this.resolveAddressId(res.data.pickupAddress)
          this.differentClientInfo = !res.data.private
            && !!customerAddressId
            && (pickupAddressId === null || customerAddressId !== pickupAddressId)

          this.applyEventDefaults()
          this.enforceMultiTransportRestrictions()
          this.syncPlanningInputsFromOrder()
          this.applyPlannerPrefill()
          this.ensurePlanningTeamDefinitionsLoaded()
          this.scheduleTransportHistoryCheck()
          this.initializeCustomerOrderTypeValidation()
        })
        .catch(err => parseRequestError(err))
    },
    applyPlannerPrefill() {
      if (!this.plannerPrefill || this.plannerPrefillApplied) {
        return
      }

      this.ensurePlanningTeamDefinitionsLoaded()

      if (this.hasExistingPlanning) {
        this.planningAssignmentOffsetInput = this.formatPlanningDurationMinutes(this.order.plannedAssignmentOffsetMinutes)
        this.planningAssignmentOffsetValidationError = ''
        this.planningDurationInput = this.formatPlanningDurationMinutes(this.order.plannedDurationMinutes)
        this.planningDurationValidationError = ''
        this.plannerPrefillApplied = true
        return
      }

      if (this.plannerPrefill.plannedTeamDefinition) {
        this.$set(this.order, 'plannedTeamDefinition', this.plannerPrefill.plannedTeamDefinition)
      }

      this.$set(this.order, 'startAt', this.plannerPrefill.startAt)
      this.$set(this.order, 'plannedAssignmentOffsetMinutes', this.plannerPrefill.plannedAssignmentOffsetMinutes)
      if (!this.hasAutoCalculatedPlanningDuration) {
        this.$set(this.order, 'plannedDurationMinutes', this.plannerPrefill.plannedDurationMinutes)
      }
      this.planningAssignmentOffsetInput = this.formatPlanningDurationMinutes(this.plannerPrefill.plannedAssignmentOffsetMinutes)
      this.planningAssignmentOffsetValidationError = ''
      this.planningDurationInput = this.formatPlanningDurationMinutes(
        this.hasAutoCalculatedPlanningDuration
          ? this.autoCalculatedPlanningDurationMinutes
          : this.plannerPrefill.plannedDurationMinutes,
      )
      this.planningDurationValidationError = ''
      this.plannerPrefillApplied = true
    },
    syncPlanningInputsFromOrder() {
      if (!this.hasExistingPlanning) {
        this.planningAssignmentOffsetInput = null
        this.planningAssignmentOffsetValidationError = ''
        if (!this.hasAutoCalculatedPlanningDuration) {
          this.planningDurationInput = null
          this.planningDurationValidationError = ''
        }
        return
      }

      this.planningAssignmentOffsetInput = this.formatPlanningDurationMinutes(this.order?.plannedAssignmentOffsetMinutes)
      this.planningAssignmentOffsetValidationError = ''

      if (this.hasAutoCalculatedPlanningDuration) {
        this.planningDurationInput = this.formatPlanningDurationMinutes(this.autoCalculatedPlanningDurationMinutes)
      } else {
        this.planningDurationInput = this.formatPlanningDurationMinutes(this.order?.plannedDurationMinutes)
      }

      this.planningDurationValidationError = ''
    },
    syncAutoCalculatedPlanningDuration() {
      if (!this.hasAutoCalculatedPlanningDuration) {
        if (!this.order?.plannedDurationMinutes) {
          this.planningDurationInput = this.formatPlanningDurationMinutes(this.order?.plannedDurationMinutes)
        }
        return
      }

      this.$set(this.order, 'plannedDurationMinutes', this.autoCalculatedPlanningDurationMinutes)
      this.planningDurationInput = this.formatPlanningDurationMinutes(this.autoCalculatedPlanningDurationMinutes)
      this.planningDurationValidationError = ''
    },
    async ensurePlanningTeamDefinitionsLoaded() {
      if (!this.showPlannerPrefillCard || this.planningTeamDefinitions.length) {
        return
      }

      try {
        const response = await teamDefinitionRequests.getTeamDefinitions({
          limit: 250,
          sortField: 'code',
          sortDir: 'asc',
        })
        const responseData = response?.data

        if (Array.isArray(responseData?.items)) {
          this.planningTeamDefinitions = responseData.items
          return
        }

        this.planningTeamDefinitions = Array.isArray(responseData) ? responseData : []
      } catch (error) {
        parseRequestError(error)
      }
    },
    handlePlanningTeamDefinitionChange(value) {
      const selectedTeamDefinition = this.planningTeamDefinitions.find(item => Number(item.id) === Number(value))

      this.$set(this.order, 'plannedTeamDefinition', selectedTeamDefinition
        ? {
          id: Number(selectedTeamDefinition.id),
          code: selectedTeamDefinition.code || '',
        }
        : null)

      this.commitOrder()
    },
    formatPlanningDurationMinutes(value) {
      return formatPlanningMinutes(value) || null
    },
    handlePlanningDurationInput(value) {
      if (typeof value !== 'string') {
        this.planningDurationValidationError = this.$t('order.planning.duration-invalid')
        this.planningDurationInput = value
        this.$set(this.order, 'plannedDurationMinutes', null)
        return
      }

      const { parsedValue: parsedDuration, normalizedValue } = normalizePlanningTimeInput(value)

      this.planningDurationInput = normalizedValue
      this.$set(this.order, 'plannedDurationMinutes', parsedDuration)
      this.planningDurationValidationError = parsedDuration
        ? ''
        : this.$t('order.planning.duration-invalid')

      if (parsedDuration) {
        this.commitOrder()
      }
    },
    handlePlanningAssignmentOffsetInput(value) {
      if (typeof value !== 'string') {
        this.planningAssignmentOffsetValidationError = this.$t('order.planning.duration-invalid')
        this.planningAssignmentOffsetInput = value
        this.$set(this.order, 'plannedAssignmentOffsetMinutes', null)
        return
      }

      const { parsedValue, normalizedValue } = normalizePlanningTimeInput(value)

      this.planningAssignmentOffsetInput = normalizedValue
      this.$set(this.order, 'plannedAssignmentOffsetMinutes', parsedValue)
      this.planningAssignmentOffsetValidationError = parsedValue !== null
        ? ''
        : this.$t('order.planning.duration-invalid')

      if (parsedValue !== null) {
        this.commitOrder()
      }
    },
    calculatePlanningDurationSelectPosition(dropdownList, component, coordinates) {
      const toggleBounds = component.$refs.toggle.getBoundingClientRect()
      const dropdownHeight = dropdownList.scrollHeight
      const viewportHeight = window.innerHeight
      const spacing = 8
      const availableBelow = viewportHeight - toggleBounds.bottom
      const shouldOpenAbove = availableBelow < Math.min(dropdownHeight, 240)
      const top = shouldOpenAbove
        ? `${window.scrollY + toggleBounds.top - dropdownHeight - spacing}px`
        : coordinates.top
      const dropdownStyle = dropdownList.style

      dropdownStyle.width = coordinates.width
      dropdownStyle.left = coordinates.left
      dropdownStyle.top = top
      dropdownStyle.zIndex = '1060'
    },
    scheduleTransportHistoryCheck() {
      clearTimeout(this.transportHistoryDebounceTimeout)

      const requestData = this.transportHistoryRequestData
      if (!requestData) {
        this.lastTransportHistoryRequestKey = null
        this.resetTransportHistory()
        return
      }

      const requestKey = JSON.stringify(requestData)
      if (requestKey === this.lastTransportHistoryRequestKey && (this.transportHistory.initialized || this.transportHistory.loading)) {
        return
      }

      const requestToken = this.bumpTransportHistoryRequestToken()
      this.transportHistoryDebounceTimeout = setTimeout(() => {
        this.lastTransportHistoryRequestKey = requestKey
        this.$store.dispatch('order/fetchOrderTransportHistory', {
          data: requestData,
          requestToken,
        }).catch(() => {})
      }, 350)
    },
    bumpTransportHistoryRequestToken() {
      this.transportHistoryRequestToken += 1
      this.$store.commit('order/SET_ORDER_TRANSPORT_HISTORY_REQUEST_TOKEN', this.transportHistoryRequestToken)

      return this.transportHistoryRequestToken
    },
    resetTransportHistory() {
      this.bumpTransportHistoryRequestToken()
      this.$store.commit('order/RESET_ORDER_TRANSPORT_HISTORY')
    },
    openTransportHistoryModal() {
      this.$store.commit('order/SET_ORDER_TRANSPORT_HISTORY_MODAL_VISIBLE', true)
    },
    closeTransportHistoryModal() {
      this.$store.commit('order/SET_ORDER_TRANSPORT_HISTORY_MODAL_VISIBLE', false)
    },
    normalizePozTransportHistoryLookbackMonths(value) {
      const months = Number(value)

      if (!Number.isInteger(months) || months < 1) {
        return 12
      }

      return months
    },
    formatTransportHistoryStartAt(value) {
      return value ? moment(value).format('YYYY-MM-DD HH:mm') : '-'
    },
    formatOrderAddress(address) {
      if (!address) {
        return null
      }

      if (typeof address === 'string') {
        return address
      }

      if (typeof address !== 'object') {
        return null
      }

      const zipCity = [address.zip, address.city].filter(Boolean).join(' ')
      const parts = [address.name, address.address || address.addressLine1, zipCity].filter(Boolean)

      return parts.length ? parts.join(', ') : null
    },
    getTransportHistoryStatusColor(status) {
      switch (status) {
        case 'received':
          return '#F7E7C4'
        case 'cancelled':
          return '#C6DAFB'
        default:
          return null
      }
    },
    saveChanges(confirm = false) {
      if (confirm && this.isCustomerOrderTypeBlockingSubmit) {
        return
      }

      this.fullValidation = confirm
      this.$nextTick(() => {
        this.$refs.editOrderValidation.validate()
          .then(valid => {
            if (!valid) {
              return false
            }

            this.lockSaveButton = true
            if (this.fullValidation) {
              this.loading = true
            }

            const orderData = this.prepareOrderData()

            if (confirm && orderData.status === 'draft') {
              orderData.status = 'received'
            }

            const payload = {
              id: orderData.id,
              data: orderData,
              allowStatusUpdate: confirm === true,
            }
            this.isSavingDraftOrder = orderData.status === 'draft'
            this.$store.dispatch('order/updateOrder', payload)
              .then(async response => {
                this.applyOrderSaveGeneratedValues(response?.data)
                await this.persistStructuredPatientInfo()
                window.toast.notify.success(this.$t('order.notify.edit.success_title'))
                if (confirm) {
                  this.redirectToView()
                }
              })
              .catch(err => parseRequestError(err))
              .finally(() => {
                this.isSavingDraftOrder = false
                this.loading = false
                this.lockSaveButton = false
              })

            return true
          })
      })
    },
    prepareOrderData() {
      const orderData = cloneNested(this.order)
      if (this.hasAutoCalculatedPlanningDuration) {
        orderData.plannedDurationMinutes = this.autoCalculatedPlanningDurationMinutes
      }
      const plannedTeamDefinitionId = orderData.plannedTeamDefinition && typeof orderData.plannedTeamDefinition === 'object'
        ? orderData.plannedTeamDefinition.id || null
        : orderData.plannedTeamDefinition || null
      orderData.plannedTeamDefinitions = plannedTeamDefinitionId ? [plannedTeamDefinitionId] : []
      delete orderData.plannedTeamDefinition
      orderData.plannedAssignmentOffsetMinutes = orderData.plannedAssignmentOffsetMinutes ?? null
      orderData.plannedDurationMinutes = orderData.plannedDurationMinutes ?? null
      orderData.customerAddress = !empty(this.order.customerAddress) ? this.order.customerAddress.id : null
      orderData.pickupAddress = !empty(this.order.pickupAddress) ? this.order.pickupAddress.id : null
      orderData.deliveryAddress = !empty(this.order.deliveryAddress) ? this.order.deliveryAddress.id : null
      orderData.doctorUser = !empty(this.order.doctorUser) ? this.order.doctorUser.id : null
      const firstComment = Array.isArray(orderData.comments) ? orderData.comments[0] : null
      if (!firstComment || !firstComment.comment) {
        orderData.comments = null
      }

      if (this.isMultiTransport) {
        orderData.returnTransport = false
      }

      // Convert per-patient addresses to IDs for MULTI_TRANSPORT
      if (this.isMultiTransport && Array.isArray(orderData.patientInfo)) {
        orderData.patientInfo = orderData.patientInfo.map(patient => {
          const updatedPatient = { ...patient }
          const { address } = updatedPatient

          updatedPatient.address = address && typeof address === 'object'
            ? address.id || null
            : address || null
          updatedPatient.addressDetails = updatedPatient.addressDetails && typeof updatedPatient.addressDetails === 'object'
            ? updatedPatient.addressDetails.id || null
            : updatedPatient.addressDetails || null

          return updatedPatient
        })
      }

      return orderData
    },
    applyOrderSaveGeneratedValues(generatedValues) {
      if (!generatedValues || typeof generatedValues !== 'object') {
        return
      }

      if (Object.prototype.hasOwnProperty.call(generatedValues, 'version')) {
        this.order.version = generatedValues.version
      }

      if (Object.prototype.hasOwnProperty.call(generatedValues, 'updatedAt')) {
        this.order.updatedAt = generatedValues.updatedAt
      }

      if (Array.isArray(generatedValues.patientInfo) && Array.isArray(this.order.patientInfo)) {
        this.order.patientInfo = this.order.patientInfo.map(patient => {
          const generatedPatient = generatedValues.patientInfo.find(item => item?.clientUid && item.clientUid === patient?.clientUid)
          if (!generatedPatient) {
            return patient
          }

          return {
            ...patient,
            id: generatedPatient.id ?? patient.id,
            version: generatedPatient.version ?? patient.version,
          }
        })
      }

      if (Array.isArray(generatedValues.comments) && Array.isArray(this.order.comments)) {
        this.order.comments = this.order.comments.map((comment, index) => {
          const generatedComment = generatedValues.comments.find(item => item?.index === index)
          if (!generatedComment) {
            return comment
          }

          return {
            ...comment,
            id: generatedComment.id ?? comment.id,
          }
        })
      }
    },
    buildStructuredPatientUpdatePayload(patient) {
      return {
        version: patient?.version,
        patientData: patient?.patientData ?? null,
        death: patient?.death ?? null,
        summary: patient?.summary ?? null,
        providedHealthServices: patient?.providedHealthServices ?? null,
        diagnosis: patient?.diagnosis ?? null,
        healthCondition: patient?.healthCondition ?? null,
      }
    },
    persistStructuredPatientInfo() {
      if (!Array.isArray(this.order?.patientInfo) || !this.order.patientInfo.length) {
        return Promise.resolve()
      }

      const requests = this.order.patientInfo
        .filter(patient => patient?.id && patient?.version != null)
        .map(patient => this.$store.dispatch('order/updateOrderPatientData', {
          id: patient.id,
          orderId: this.order.id,
          data: this.buildStructuredPatientUpdatePayload(patient),
        }))

      return Promise.all(requests)
    },
    commitOrder() {
      this.$store.commit('order/SET_ORDER_EDITED', this.order)
      this.unsavedChanges = true
      if (this.order.status === 'draft') {
        this.setSaveChangesTimeout()
      }
    },
    setSaveChangesTimeout() {
      clearTimeout(this.saveChangesTimout)
      this.lockSaveButton = true
      this.saveChangesTimout = setTimeout(() => {
        this.saveChanges()
      }, 2500)
    },
    isDisabled(notForStatus = [], notForRoles = []) {
      if (this.order.status === 'draft') {
        return false
      }

      if (guard.hasAtLeastOneRole(this.me.roles, ['ROLE_ADVANCED_DISPATCHER'])) {
        return false
      }

      if (notForStatus.includes(this.order.status) && guard.hasAtLeastOneRole(this.me.roles, notForRoles)) {
        return false
      }

      return true
    },
    handleDifferentClientInfo(differentClientInfo) {
      if (!differentClientInfo) {
        if (this.order.status !== 'draft' && this.order.pickupAddress) {
          this.order.customerAddress = this.order.pickupAddress
        } else {
          this.order.customerAddress = {}
        }
        this.commitOrder()
      }
    },
    syncCustomerOrderTypeAvailabilities() {
      if (!this.isCustomerOrderTypeValidationEnabled) {
        this.customerOrderTypeAvailabilityCustomerId = null
        this.customerOrderTypeAvailabilityResolvedCustomerId = null
        this.customerOrderTypeAvailabilityLoading = false
        this.customerOrderTypeAvailabilities = []
        return
      }

      const customerId = this.customerOrderTypeValidationCustomerId

      if (!customerId) {
        this.customerOrderTypeAvailabilityCustomerId = null
        this.customerOrderTypeAvailabilityResolvedCustomerId = null
        this.customerOrderTypeAvailabilityLoading = false
        this.customerOrderTypeAvailabilities = []
        return
      }

      if (this.customerOrderTypeAvailabilityCustomerId === customerId) {
        return
      }

      this.customerOrderTypeAvailabilityCustomerId = customerId
      this.customerOrderTypeAvailabilityResolvedCustomerId = null
      this.customerOrderTypeAvailabilityLoading = true
      const request = this.$store.dispatch('customer/fetchCustomerOrderTypeAvailabilitiesByCustomerId', customerId)
      if (!request || typeof request.then !== 'function') {
        this.customerOrderTypeAvailabilityLoading = false
        this.customerOrderTypeAvailabilities = []
        return
      }

      request
        .then(response => {
          if (this.customerOrderTypeAvailabilityCustomerId !== customerId) {
            return
          }

          this.customerOrderTypeAvailabilityResolvedCustomerId = customerId
          this.customerOrderTypeAvailabilityLoading = false
          this.customerOrderTypeAvailabilities = Array.isArray(response.data) ? response.data : []
        })
        .catch(error => {
          this.customerOrderTypeAvailabilityResolvedCustomerId = customerId
          this.customerOrderTypeAvailabilityLoading = false
          this.customerOrderTypeAvailabilities = []
          parseRequestError(error)
        })
    },
    initializeCustomerOrderTypeValidation() {
      this.customerOrderTypeValidationInitialized = true
      this.syncCustomerOrderTypeAvailabilities()
    },
    handleIsPrivate() {
      if (!this.order.private) {
        this.order.paymentMethod = null
        this.order.amountGross = null
        this.commitOrder()
      } else {
        this.order.commissionedBy = null
        this.order.agreedWith = null
        this.differentClientInfo = false
        this.commitOrder()
      }
    },
    addPatient() {
      if (!Array.isArray(this.order.patientInfo)) {
        this.$set(this.order, 'patientInfo', [])
      }
      this.order.patientInfo.push(createEmptyPatientInfo())
      this.commitOrder()
    },
    removePatient(index) {
      if (!Array.isArray(this.order.patientInfo)) {
        return
      }

      const patient = this.order.patientInfo[index]
      if (!patient) {
        return
      }

      // Unsaved patients (without id) can be removed physically from FE state,
      // even when order is already non-draft.
      if (this.order.status === 'draft' || !patient.id) {
        this.order.patientInfo.splice(index, 1)
        this.commitOrder()
        return
      }

      this.$set(patient, 'patientTransportStatus', patientTransportStatuses.removedAfterConfirmation)
      this.$set(patient, 'patientOnBoard', false)
      this.commitOrder()
    },
    restorePatient(index) {
      if (!Array.isArray(this.order.patientInfo)) {
        return
      }

      const patient = this.order.patientInfo[index]
      if (!patient) {
        return
      }

      const currentStatus = resolvePatientTransportStatus(patient)
      if (currentStatus === patientTransportStatuses.removedAfterConfirmation) {
        this.$set(patient, 'patientTransportStatus', patientTransportStatuses.pending)
      }
      this.commitOrder()
    },
    convertFromAnnual(patient) {
      if (!patient || patient.age === null || patient.age === undefined || patient.age === '') {
        return
      }

      const ageString = patient.age.toString()
      const yearShort = parseInt(moment().format('YY'), 10)
      const yearFull = parseInt(moment().format('YYYY'), 10)
      let nextAge

      if (ageString.length < 3) {
        nextAge = yearShort < patient.age
          ? yearShort - patient.age + 100
          : yearShort - patient.age
      } else {
        nextAge = yearFull - patient.age
      }

      this.$set(patient, 'age', nextAge)
      this.commitOrder()
    },
    confirmDelete() {
      this.loading = true

      this.$bvModal
        .msgBoxConfirm(
          this.$t('order.confirm-delete-draft.description'),
          {
            title: this.$t('order.confirm-delete-draft.title'),
            okTitle: this.$t('shared.yes'),
            cancelTitle: this.$t('shared.no'),
            okVariant: 'danger',
            cancelVariant: 'outline-secondary',
          },
        )
        .then(answer => {
          if (answer) {
            this.$store.dispatch('order/deleteOrder', this.order.id)
              .then(() => {
                this.$router.back()
                window.toast.notify.success(this.$t('order.confirm-delete-draft.notify-success'))
              })
          } else {
            this.loading = false
          }
        })
    },
    redirectToView() {
      this.$router.push(`/order/view/${this.$route.params.id}`)
    },
  },
}
</script>

<style lang="scss" scoped>
.card-justify {
  height: calc(100% - 2rem) !important;
}

::v-deep .transport-history-modal .modal-dialog {
  max-width: 920px;
}

.transport-history-table th,
.transport-history-table td {
  vertical-align: top;
}
</style>
